import { createQueryApi } from '@/shared/features/query/infra/adapters/rtk.adapter';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { BrowsingHistoryRepository } from '../../domain/interfaces/BrowsingHistoryRepository';
import type {
  BrowsingHistoryCategoryList,
  BrowsingHistoryVisitorDataResponse,
} from '../../domain/models/BrowsingHistory';
import type {
  BrowsingHistoryCategoriesResponseDTO,
  BrowsingHistoryVisitorDataResponseDTO,
} from '../dto/browsingHistory.dto';
import {
  adaptCategoriesDtoToDomain,
  adaptVisitorDataDtoToDomain,
} from '../mappers/browsingHistory.mapper';
import type { AppDispatch } from '@/app/store/store';

const _browsingHistoryApi = createQueryApi({ clientId: 'browsingHistoryApi' });

const browsingHistoryRtkApi = _browsingHistoryApi.injectEndpoints({
  endpoints: (builder) => ({
    getVisitorData: builder.query<
      BrowsingHistoryVisitorDataResponseDTO,
      { category?: string; page?: number }
    >({
      query: ({ category, page }) => ({
        url: '/recently-viewed/visitor-data/',
        method: 'GET',
        params: {
          ...(category ? { category } : {}),
          ...(page !== undefined ? { page } : {}),
        },
        withCredentials: true,
      }),
    }),
    getCategories: builder.query<BrowsingHistoryCategoriesResponseDTO, void>({
      query: () => ({
        url: '/recently-viewed/categories/',
        method: 'GET',
        withCredentials: true,
      }),
    }),
  }),
});

export const browsingHistoryApi = browsingHistoryRtkApi;
export const browsingHistoryApiRedux = _browsingHistoryApi as unknown as ReduxQueryClient;

export class BrowsingHistoryAdapter implements BrowsingHistoryRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async getVisitorData(category?: string, page?: number): Promise<BrowsingHistoryVisitorDataResponse> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK mutation result typing
    const result = await (this.dispatch as any)(
      browsingHistoryRtkApi.endpoints.getVisitorData.initiate({ category, page }),
    );
    if ('error' in result) throw result.error;
    return adaptVisitorDataDtoToDomain(result.data as BrowsingHistoryVisitorDataResponseDTO);
  }

  async getCategories(): Promise<BrowsingHistoryCategoryList> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK mutation result typing
    const result = await (this.dispatch as any)(
      browsingHistoryRtkApi.endpoints.getCategories.initiate(undefined),
    );
    if ('error' in result) throw result.error;
    const dto = result.data as BrowsingHistoryCategoriesResponseDTO;
    const adapted = adaptCategoriesDtoToDomain(dto);
    return {
      categories: ['All', ...adapted.categories.filter((c) => c !== 'All')],
    };
  }
}
